import { computed, inject, Injectable } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { distinctUntilChanged, map, tap } from "rxjs/operators";
import { ClassroomFacade, createBookpageDisplaySlide } from "shared/classroom/data-access";
import { fromToolbar, ToolbarActions } from "shared/classroom/toolbar/data-access";
import { Store } from "@ngrx/store";
import { BookpageDialogService } from "../components/bookpage-dialog/bookpage-dialog.service";

@Injectable()
export class BookpageEffects {

    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly bookpageDialog = inject(BookpageDialogService);
    private readonly lessonYjs = inject(ClassroomFacade);

    private readonly primarySlide$ = toObservable(this.lessonYjs.primarySlide);
    private readonly isBookpageToolActivateable$ = toObservable(
        computed(() => (this.lessonYjs.primarySlide()?.bookpages?.length ?? 0) > 0)
    );

    readonly setBookpageToolActivateableState$ = createEffect(() =>
        this.isBookpageToolActivateable$.pipe(
            distinctUntilChanged(),
            map((isActivateable) =>
                isActivateable
                    ? ToolbarActions.enableBookpageTool()
                    : ToolbarActions.disableBookpageTool()
            )
        )
    );

    readonly showBookpageListDialog = createEffect(() =>
        this.actions$.pipe(
            ofType(ToolbarActions.toggleBookpageTool),
            concatLatestFrom(() =>
                this.store.select(fromToolbar.isBookpageToolActivated)
            ),
            map(([, bookpageToolActivated]) =>
                bookpageToolActivated
                    ? ToolbarActions.showBookpageListDialog()
                    : ToolbarActions.closeBookpageListDialog()
            )
        )
    );

    readonly showBookpageDialogWhenToolGetsEnabled = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.showBookpageListDialog),
                concatLatestFrom(() => this.primarySlide$),
                tap(([, slide]) =>
                    this.bookpageDialog.open(slide?.bookpages || [])
                )
            ),
        { dispatch: false }
    );

    /**
     * Whenever a bookpage has been selected,
     * it should be shown as secondary slide inside the preview screen.
     */
    readonly showBookpageInPreviewScreenOnBookpageSelection$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ToolbarActions.bookpageHasBeenSelected),
                tap(({ bookpage }) =>
                    this.lessonYjs.showSlide(2, createBookpageDisplaySlide(bookpage))
                )
            ),
        { dispatch: false }
    );
}
